import { RegionShared } from "@1games-ru/region-shared";
import { callClient, callServer } from "@/core/helpers/callEvent.ts";
import {
  GetPlayersRequest,
  GetPlayersResponse,
} from "@/core/rpc-mp-provider/messages/get-players.ts";
import { RpcMpProviderFailedResponse } from "@/core/rpc-mp-provider/responses/rpc-mp-provider-failed-response.ts";
import { RpcMpProviderSuccessResponse } from "@/core/rpc-mp-provider/responses/rpc-mp-provider-success-response.ts";
import RpcMpProvider from "@/core/rpc-mp-provider/rpc-mp-provider.ts";
import { TattooParlorEvent } from "@/domains/tattooParlor/constants/tattooParlorEvent.ts";

export default class PlayerMpProvider {
  public static async getPlayer(): Promise<
    | RpcMpProviderFailedResponse
    | RpcMpProviderSuccessResponse<typeof RegionShared.Client.Responses.GetPlayerResponse>
  > {
    return RpcMpProvider.getInstance().callClient<
      typeof RegionShared.Client.Requests.GetPlayerRequest,
      typeof RegionShared.Client.Responses.GetPlayerResponse
    >(RegionShared.Client.Events.Player.GetPlayer, {});
  }

  public static async getPlayers(
    playerIds: number[],
    characterIds: number[],
  ): Promise<RpcMpProviderFailedResponse | RpcMpProviderSuccessResponse<GetPlayersResponse>> {
    return RpcMpProvider.getInstance().callClient<GetPlayersRequest, GetPlayersResponse>(
      "PlayerGetPlayersAnyToClient",
      {
        playerIds,
        characterIds,
      },
    );
  }

  public static async getCharacter(characterId: number) {
    const result = await PlayerMpProvider.getCharacters([characterId]);

    if (result instanceof RpcMpProviderFailedResponse) {
      throw new Error("Get Character failed response");
    }

    if (result.data[characterId]) {
      return result.data[characterId].character;
    }

    return null;
  }

  public static async getCharacters(
    characterIds: number[],
  ): Promise<
    | RpcMpProviderFailedResponse
    | RpcMpProviderSuccessResponse<typeof RegionShared.Client.Responses.GetPlayersResponse>
  > {
    return RpcMpProvider.getInstance().callClient<
      typeof RegionShared.Client.Requests.GetPlayersRequest,
      typeof RegionShared.Client.Responses.GetPlayersResponse
    >(RegionShared.Client.Events.Player.GetPlayers, {
      characterIds,
    });
  }

  public static async getCharacterAuthToken(): Promise<
    RpcMpProviderSuccessResponse<string> | RpcMpProviderFailedResponse
  > {
    return RpcMpProvider.getInstance().callServer(
      RegionShared.Backend.Events.Character.GetCharacterToken,
      {},
    );
  }

  public static setHair(index: number) {
    return callClient(
      RegionShared.Events.CreateCharacter.SetHair,
      {
        drawableId: index,
      },
      { noRet: true },
    );
  }

  public static setHeadOverlayDefault() {
    return callClient(
      RegionShared.Events.CreateCharacter.SetHeadOverlayDefault,
      {},
      { noRet: true },
    );
  }

  public static setHeadOverlay(
    overlayId: number,
    index: number,
    opacity: number,
    firstColor: number,
    secondColor: number,
  ) {
    return callClient(
      RegionShared.Events.CreateCharacter.SetHeadOverlay,
      {
        overlayId,
        index,
        opacity,
        firstColor,
        secondColor,
      },
      { noRet: true },
    );
  }

  public static setHairDefault() {
    return callClient(RegionShared.Events.CreateCharacter.SetHairDefault, {}, { noRet: true });
  }

  public static resetTattoos() {
    return callClient(TattooParlorEvent.ResetClientTattoos);
  }

  public static async setClientTattoo(collection: string, overlay: string) {
    callClient(TattooParlorEvent.SetClientTattoo, {
      collection,
      overlay,
    });
  }

  public static async removeClientTattoo(collection: string, overlay: string) {
    callClient(TattooParlorEvent.RemoveTattoo, {
      collection,
      overlay,
    });
  }

  public static async buyServerTattoo(collection: string, overlay: string) {
    callServer(+TattooParlorEvent.BuyTattoo, {
      collection,
      overlay,
    });
  }

  public static async removeServerTattoo(collection: string, overlay: string) {
    callServer(+TattooParlorEvent.RemoveTattoo, {
      collection,
      overlay,
    });
  }
}
